import React, { useState, useRef, useEffect } from 'react'
import { Route, BrowserRouter as Router, Routes, Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ReCAPTCHA from "react-google-recaptcha";
import config from '../Config';

import { ContactUsService } from '../services/ContactUsService';
import IntlTelInput from 'react-intl-tel-input';
import { parsePhoneNumberFromString, getExampleNumber, CountryCode } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";

type FormData = {
  contact_type: string,
  hear_about_us: string,
  first_name: string,
  last_name: string,
  phone_code: any,
  email: string,
  phone_number: string,
  message: string,
  selectedCountry?: CountryCode
};

type FormErrors = {
  contact_type?: string,
  hear_about_us?: string,
  first_name?: string,
  last_name?: string,
  phone_code?: string,
  email?: string,
  phone_number?: string,
  message?: string,
  captcha?: string,
  common_err?: string,
  success?: string
};

export const Banner = () => {

  const allowedRef = ["General Inquiry", "Integrations", "Issuing", "IT Solutions", "Job", "Billing", "Bug Reporting", "Compliance", "Hardware", "Portal", "Statements", "Support"];

  const [phoneValid, setPhoneValid] = useState<boolean | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("US");
  const [maxLength, setMaxLength] = useState<number>(16);
  const [formData, setFormData] = useState<FormData>({
    contact_type: 'General Inquiry',
    hear_about_us: '',
    first_name: '',
    last_name: '',
    phone_code: '',
    email: '',
    phone_number: '',
    message: ''
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const captchaRef = useRef<ReCAPTCHA | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChange2 = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChange3 = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
    setFormErrors({ captcha: '' });
  };



  const validate = () => {
    const errors: FormErrors = {};

    if (!formData.contact_type) {
      errors.contact_type = 'Contact type is required';
    } else {
      if (allowedRef.includes(formData.contact_type) && (!formData.hear_about_us || formData.hear_about_us == '')) {
        errors.hear_about_us = 'How did you hear about us is required';
      }
    }

    if (!formData.first_name) {
      errors.first_name = 'First name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.first_name)) {
      errors.first_name = 'First name is invalid';
    }

    if (!formData.last_name) {
      errors.last_name = 'Last name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.last_name)) {
      errors.last_name = 'Last name is invalid';
    }

    if (!formData.email) {
      errors.email = 'Email address is required';
    } else if (!/^[a-zA-Z][_a-zA-Z0-9-]+(\.[_a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,10})$/.test(formData.email)) {
      errors.email = 'Email address is invalid';
    }

    if (formData.phone_number) {
      const onlyNumbers = formData.phone_number.replace(/\D/g, "");
      if (!/^[0-9+]+$/.test(onlyNumbers)) {
        errors.phone_number = 'Phone number is invalid';
      } else if (onlyNumbers.length < 9) {
        errors.phone_number = 'Phone number should be minimum 9 digits';
      } else if (onlyNumbers.length > 11) {
        errors.phone_number = 'Phone number should be maximum 11 digits';
      }

    }

    if (formData.phone_number) {
      const digitsOnly = formData.phone_number.replace(/\D/g, "");

      if (digitsOnly.length > maxLength) {
        errors.phone_number = `Phone number must be at most ${maxLength} digits`;
      }
    }

    if (!formData.message) {
      errors.message = 'Message is required';
    } else if (!/^[0-9a-zA-Z.@),(\]\[('":_-\s]+$/.test(formData.message)) {
      errors.message = 'Message is invalid';
    } else if (formData.message.length > 4000) {
      errors.message = 'Message must not exceed 4000 characters';
    }

    if (!captchaValue) {
      errors.captcha = 'Captcha is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const submitBtnElement = document.getElementById("submitBtn");
    if (submitBtnElement) {
      submitBtnElement.classList.add("disabled");
    }

    if (captchaRef.current) {
      captchaRef.current.reset();
      setCaptchaValue(null);
    }

    if (validate()) {
      ContactUsService.sendContactFormData(formData)
        .then((res) => {
          const apiResponse: any = res.data;
          if (apiResponse) {
            const response: any = apiResponse.response;
            if (response.code === '00') {
              setFormData({
                contact_type: 'General Inquiry',
                hear_about_us: '',
                first_name: '',
                last_name: '',
                phone_code: '',
                email: '',
                phone_number: '',
                message: ''
              });
              setFormErrors({ success: response.description });
              const intervalId = setInterval(() => {
                setFormErrors({ success: '' });
                clearInterval(intervalId);
              }, 10000);
            } else {
              setFormErrors({
                ...formErrors,
                common_err: response.description,
              });
            }
          }

          if (submitBtnElement) {
            submitBtnElement.classList.remove("disabled");
          }
        }
        )
        .catch(() => {
          if (submitBtnElement) {
            submitBtnElement.classList.remove("disabled");
          }
        });
    } else {
      if (submitBtnElement) {
        submitBtnElement.classList.remove("disabled");
      }
    }
  };

  function formatPhoneNumber(num: string): string {

    const dialCode = document.querySelector(".selected-dial-code")?.innerHTML;
    setFormData({
      ...formData,
      phone_code: dialCode
    });
    // remove non-digits first
    const digits = num.replace(/\D/g, "");
    if (digits.length == 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 11) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else {
      return digits;
    }
    // if (digits.length >0 && digits.length <= 3) {
    //   return `(${digits})`;
    // } else if (digits.length >3 && digits.length <= 6) {
    //   return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    // } else if (digits.length >6 && digits.length <= 9) {
    //   return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
    // } else if (digits.length >6 && digits.length <= 10) {
    //   return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    // } else {
    //   return ``;
    // }
  }

  return (
    <>
      <section className='contact-bg'>

        <section className='contactus-bx gradient-backgroundfull'>
          <img src={`${config.siteBaseUrl}/assets/imgs/contct-dots.svg`} alt='' className='cdots' />
          <img src={`${config.siteBaseUrl}/assets/imgs/our-integrations-bg.svg`} alt="" className="cdots1" />
          <Container className='mvcontainer'>
            <Row>
              <Col xs lg="6">
                <div className='contact-bx'>
                  <div className='contact-txt'>
                    <img src={`${config.siteBaseUrl}/assets/imgs/contact-icn1.svg`} alt='' /> Let’s talk
                  </div>
                  <div className='contact-txt1'>Our team will be happy to answer your questions. Fill out the form below and we will be in touch as soon as possible. By sending a message, you agree to receive emails from us, including exclusive offers and updates.</div>
                  <form id="contactForm" name="contactForm" className="contactForm" onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-12">
                        <Form.Group className="mb-3">
                          <Form.Select aria-label="Default select example" onChange={handleChange2} name='contact_type'>
                            <option value="General Inquiry">General Inquiry</option>
                            <option value="Integrations">Integrations</option>
                            <option value="Issuing">Issuing</option>
                            <option value="IT Solutions">IT Solutions</option>
                            <option value="Job">Jobs</option>
                            <option value="Billing">Billing</option>
                            <option value="Bug Reporting">Bug Reporting</option>
                            <option value="Compliance">Compliance</option>
                            <option value="Hardware">Hardware</option>
                            <option value="Portal">Portal</option>
                            <option value="Statements">Statements</option>
                            <option value="Support">Support</option>

                          </Form.Select>
                          {formErrors.contact_type && <span style={{ color: 'red' }}>{formErrors.contact_type}</span>}
                        </Form.Group>

                      </div>
                      {/* <div className="col-md-6">
                          <Form.Group className="mb-3">
                          <Form.Control type="text" name='company_name' maxLength={50} value={formData.company_name} onChange={handleChange} placeholder="Company Name" />
                          {formErrors.company_name && <span style={{ color: 'red' }}>{formErrors.company_name}</span>}
                          </Form.Group>
                        </div> */}
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Control type="text" name='first_name' maxLength={50} autoComplete='off' value={formData.first_name} onChange={handleChange} placeholder="First Name*" />
                          {formErrors.first_name && <span style={{ color: 'red' }}>{formErrors.first_name}</span>}
                        </Form.Group>

                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Control type="text" name='last_name' maxLength={50} autoComplete='off' value={formData.last_name} onChange={handleChange} placeholder="Last Name*" />
                          {formErrors.last_name && <span style={{ color: 'red' }}>{formErrors.last_name}</span>}
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Control type="text" name='email' maxLength={100} autoComplete='off' value={formData.email} onChange={handleChange} placeholder="Email Address*" />
                          {formErrors.email && <span style={{ color: 'red' }}>{formErrors.email}</span>}
                        </Form.Group>

                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3">

                          <IntlTelInput
                            containerClassName="intl-tel-input"
                            autoComplete="off"
                            inputClassName="form-control"
                            fieldName="phone_number"
                            value={formData.phone_number}
                            onPhoneNumberChange={(isValid, value, countryData) => {
                              const countryISO = countryData?.iso2?.toUpperCase() as CountryCode;
                              const onlyNumbers = formatPhoneNumber(value);

                              setFormData((prev) => ({
                                ...prev,
                                phone_number: onlyNumbers,
                                phone_code: countryData?.dialCode ? `+${countryData.dialCode}` : prev.phone_code,
                              }));

                              setSelectedCountry(countryISO);
                              setMaxLength(16);

                              try {
                                const phoneNumber = parsePhoneNumberFromString(value, countryISO);
                                setPhoneValid(phoneNumber?.isValid() ?? false);
                              } catch {
                                setPhoneValid(false);
                              }
                            }}
                            onPhoneNumberBlur={(isValid, value, countryData) => {
                              // ✅ This will fire even if autofill is used
                              setFormData((prev) => ({
                                ...prev,
                                phone_number: value,
                                phone_code: countryData?.dialCode ? `+${countryData.dialCode}` : prev.phone_code,
                              }));
                            }}
                            preferredCountries={['us', 'gb', 'in']}
                            separateDialCode={true}
                            nationalMode={false}
                            format={false}
                            telInputProps={{
                              placeholder: "Enter phone number",
                              maxLength: maxLength,
                            }}
                          />

                          {formErrors.phone_number && <span style={{ color: 'red' }}>{formErrors.phone_number}</span>}
                        </Form.Group>

                      </div>

                      <div className="col-md-12">
                        <Form.Group className="mb-3">
                          <Form.Control as="textarea" name='message' autoComplete='off' maxLength={4000} value={formData.message} rows={6} onChange={handleChange} placeholder="Your Message" />
                          {formErrors.message && <span style={{ color: 'red' }}>{formErrors.message}</span>}
                        </Form.Group>

                      </div>
                      <div className="col-md-12">
                        {(allowedRef.includes(formData.contact_type)) && (
                          <Form.Group className="mb-3">
                            <Form.Select aria-label="Default select example" onChange={handleChange3} name='hear_about_us'>
                              <option value="">How did you hear about us?</option>
                              <option value="Google Search">Google Search</option>
                              <option value="LinkedIn">LinkedIn</option>
                              <option value="Instagram">Instagram</option>
                              <option value="Reddit">Reddit</option>
                              <option value="Online ad">Online ad</option>
                              <option value="Blog or article">Blog or article</option>
                              <option value="Podcast">Podcast</option>
                              <option value="Referral from a friend or colleague">Referral from a friend or colleague</option>
                              <option value="Other">Other</option>

                            </Form.Select>
                            {formErrors.hear_about_us && <span style={{ color: 'red' }}>{formErrors.hear_about_us}</span>}
                          </Form.Group>
                        )}

                      </div>
                      <div className="col-md-6 mb-3">
                        <ReCAPTCHA
                          sitekey={config.gCaptchaSiteKey}
                          onChange={handleCaptchaChange}
                          ref={captchaRef}
                        />
                        {formErrors.captcha && <span style={{ color: 'red' }}>{formErrors.captcha}</span>}
                      </div>

                      <div className="col-md-12">
                        <Form.Group>
                          <Button type="submit" id="submitBtn" className="btn btn-warning w-100">Send Message</Button>
                        </Form.Group>
                      </div>
                      <div className="col-md-12 mt-3">
                        {formErrors.common_err && <span style={{ color: 'red' }}>{formErrors.common_err}</span>}
                        {formErrors.success && <span className='frmcd'>{formErrors.success}</span>}
                      </div>
                    </div>
                  </form>

                </div>
              </Col>
              <Col xs lg="6">
                <div className='contact-rgtbx'>
                  <img src={`${config.siteBaseUrl}/assets/imgs/contact-icn2.svg`} alt='' />
                  <h4>Interested in opening a TCB Pay account?</h4>
                  <p>See our general <Link to="/faq">Frequently Asked Questions</Link> and/or contact our Sales team using the contact form.</p>
                </div>
                <div className='contact-rgtbx'>
                  <img src={`${config.siteBaseUrl}/assets/imgs/contact-icn3.svg`} alt='' />
                  <h4>Schedule a free consultation</h4>
                  <p>Schedule a free consult with one of our experts to get a custom assessment of your business needs. We will answer any questions you may have.
                    <br /><br /><Link to={`${config.siteBaseUrl}#show-calendly`}>Schedule Now</Link></p>
                </div>
                <div className='contact-rgtbx1'>
                  <img src={`${config.siteBaseUrl}/assets/imgs/contact-icn4.svg`} alt='' />
                  <h4>Need assistance?</h4>
                  <p>If you already have a TCB Pay account, you may visit our <Link to="/wiki-portal">Portal</Link> and <Link to="/wiki-issuing">Issuing</Link> Wikis, fill out the form, email our customer care team at <Link to="mailto:support@tcbpay.com">support@tcbpay.com</Link>, or call/text us at <Link to="tel:+1 (866) 444-8585">+1 (866) 444-8585</Link>.</p>
                </div>
              </Col>
            </Row>
          </Container>


          <section className='pp-contentbx '>
            <div className='container'>
              <div className='row d-flex justify-content-center'>
                <div className='col-md-12'>
                  <div className='contact-title mb-40'> Our locations</div>
                </div>

                <div className="column">
                  <div className="effect-1">
                    <div className="effect-img">
                      <img src={`${config.siteBaseUrl}/assets/imgs/contact-pic1.png`} alt="" />
                      <div className='bluebxpic'></div>
                    </div>
                    <div className="effect-text">
                      <div className='text-eftnew'>
                        <p>Headquarters</p>
                        <h6>Boca Raton, FL</h6>
                        <span>433 Plaza Real<br /> Suite 275 <br /> Boca Raton, FL 33432</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* <div className="column">
                      <div className="effect-1">
                          <div className="effect-img">
                              <img src={`${config.siteBaseUrl}/assets/imgs/contact-pic2.png`} alt="" />
                              <div className='yelwbxpic'></div>
                          </div>
                          <div className="effect-text yelw">
                            <div className='text-eftnew'>
                              <p>Satellite Office</p>
                              <h6>Toronto</h6>
                              <span>10 Four Season Place<br/> Suite 1000<br/> Toronto, ON M9B 6H7, Canada</span>
                            </div>
                          </div>
                      </div>
                  </div> */}
                <div className="column">
                  <div className="effect-1">
                    <div className="effect-img">
                      <img src={`${config.siteBaseUrl}/assets/imgs/contact-pic3.png`} alt="" />
                      <div className='bluebxpic'></div>
                    </div>
                    <div className="effect-text">
                      <div className='text-eftnew'>
                        <p>Satellite Office</p>
                        <h6>Henderson, NV</h6>
                        <span>871 Coronado Center Dr<br /> Suite 200<br /> Henderson, NV 89052</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* <div className="column">
                      <div className="effect-1">
                          <div className="effect-img">
                              <img src={`${config.siteBaseUrl}/assets/imgs/contact-pic4.png`} alt="" />
                              <div className='yelwbxpic'></div>
                          </div>
                          <div className="effect-text yelw">
                            <div className='text-eftnew'>
                              <p>Satellite Office</p>
                              <h6>Kolkata</h6>
                              <span> SIDCO Global Tower 16th Floor, <br/>Suite 1604 CN Block, Sector V, <br/> West Bengal 700091, India</span>
                            </div>    
                          </div>
                      </div>
                  </div> */}
                {/* <div className="column">
                      <div className="effect-1">
                          <div className="effect-img">
                              <img src={`${config.siteBaseUrl}/assets/imgs/contact-pic5.png`} alt="" />
                              <div className='bluebxpic'></div>
                          </div>
                          <div className="effect-text">
                            <div className='text-eftnew'>
                              <p>Satellite Office</p>
                              <h6>Mexico City</h6>
                              <span>Av. Presidente Masaryk 111, Piso 1<br/> 
                              Col. Polanco V Seccion, Del. Miguel<br/> 
                              Hidalgo, Ciudad de Mexico, C.P. 11560</span>
                            </div>    
                          </div>
                      </div>
                  </div> */}
                {/* <div className="column">
                      <div className="effect-1">
                          <div className="effect-img">
                              <img src={`${config.siteBaseUrl}/assets/imgs/contact-pic6.png`} alt="" /> 
                              <div className='yelwbxpic'></div>
                          </div>
                          <div className="effect-text yelw">
                            <div className='text-eftnew'>
                              <p>Satellite Office</p>
                              <h6>Malta</h6>
                              <span>Tower Business Centre,<br/> 2nd floor, Tower Street,<br/>Swatar, BKR4013</span>
                            </div>   
                          </div>
                      </div>
                  </div> */}


              </div>
            </div>
          </section>
          <section className='pp-contentbx pb-180'>
            <div className='container'>
              <div className='row d-flex justify-content-between'>
                <div className='col-md-12'>
                  <div className='contact-title'> Follow us on social media</div>
                </div>
                <div className='col-md-3'>
                  <div className='contact-infobx'>
                    <div className='contact-infoicon'>
                      <img src={`${config.siteBaseUrl}/assets/imgs/contact-icon1.svg`} alt="" />
                    </div>
                    <p>@TCBPay</p>
                    <Link to="https://www.instagram.com/tcbpay/?igshid=ky6kgcgfmlh0" target='_blank' className='contact-followbtn'>Follow us now</Link>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='contact-infobx'>
                    <div className='contact-infoicon'>
                      <img src={`${config.siteBaseUrl}/assets/imgs/contact-icon2.svg`} alt="" />
                    </div>
                    <p>TCB Pay Business Solutions</p>
                    <Link to="https://www.linkedin.com/company/tcbpay/" target='_blank' className='contact-followbtn'>Follow us now</Link>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='contact-infobx'>
                    <div className='contact-infoicon'>
                      <img src={`${config.siteBaseUrl}/assets/imgs/contact-icon3.svg`} alt="" />
                    </div>
                    <p>@TCBPay</p>
                    <Link to="https://www.youtube.com/@TCBPay?app=desktop" target='_blank' className='contact-followbtn'>Follow us now</Link>
                  </div>
                </div>



              </div>
            </div>
          </section>

          <img src={`${config.siteBaseUrl}/assets/imgs/blue-lowerdots.svg`} alt="" className="orng-dots" />
        </section>

      </section>
    </>
  )
}
